import {ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {PushDelegationModel} from '../../../models/api/pushDelegationModel';
import {ThunkAction} from '../../appThunk';
import {fetchTagsAction} from '../customViews/customViewsActions';
import {updateDataAction} from '../dataGroupsActions';

/*
 * Action types.
 */

export enum DelegationsActionsEnum {
  DELEGATION_UPDATED = '@@data/delegations/DELEGATION_UPDATED',
  DELEGATION_DELETED = '@@data/delegations/DELEGATION_DELETED',
}

/*
 * Types.
 */

export interface DelegationUpdatedAction extends ActionWithPayload<PushDelegationModel> {
  type: DelegationsActionsEnum.DELEGATION_UPDATED;
}

export interface DelegationDeletedAction extends ActionWithPayload<number> {
  type: DelegationsActionsEnum.DELEGATION_DELETED;
}

/*
 * Action creators.
 */

export function updateDelegation(newPushDelegation: PushDelegationModel): ThunkAction {
  return async (dispatch, getState) => {
    // Find the existing Delegation.
    const {data} = getState();
    const newDelegation = newPushDelegation.delegation;
    const existingDelegation = data.delegations[newDelegation.id];
    const isDelegate = newDelegation.teammateIds.includes(data.userId);

    // No need to do anything if the new Delegation is older.
    if (existingDelegation && existingDelegation.updatedAt > newDelegation.updatedAt) {
      return null;
    }

    // To simplify, we currently broadcast delegation updates to the entire company.
    if (!existingDelegation && !isDelegate) {
      return null;
    }

    // No longer delegate: remove delegation.
    if (existingDelegation && !isDelegate) {
      const deleteAction: DelegationDeletedAction = {
        type: DelegationsActionsEnum.DELEGATION_DELETED,
        payload: newDelegation.id,
      };
      return dispatch(deleteAction);
    }

    // Find if we're missing tags.
    const missingTagIds = newDelegation.customViewIds.filter((id) => !data.tags[id]);
    if (missingTagIds.length > 0) {
      await dispatch(fetchTagsAction(missingTagIds));
    }

    return batch(() => {
      // Update delegations and custom views in the store.
      const updateDelegationAction: DelegationUpdatedAction = {
        type: DelegationsActionsEnum.DELEGATION_UPDATED,
        payload: {
          ...newPushDelegation,
          delegation: {...existingDelegation, ...newDelegation},
        },
      };
      dispatch(updateDelegationAction);

      // Update inboxes in the store.
      dispatch(updateDataAction({inboxes: newPushDelegation.inboxes}));
    });
  };
}

export function deleteDelegation(id: number): ThunkAction {
  return (dispatch, getState) => {
    // If we don't have this Delegation, nothing to do.
    const existingDelegation = getState().data.delegations[id];
    if (!existingDelegation) {
      return null;
    }

    const action: DelegationDeletedAction = {
      type: DelegationsActionsEnum.DELEGATION_DELETED,
      payload: id,
    };
    return dispatch(action);
  };
}

/*
 * Union type.
 */

export type DelegationsAction = DelegationUpdatedAction | DelegationDeletedAction;
